// =====================
// Device OS (iOS / Android — Mac uses iOS logo)
// =====================

;(function l_setDeviceOs() {
	const root = document.documentElement
	if (root.dataset.deviceOs) return

	const ua = navigator.userAgent
	const isIOSDevice =
		/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
	const isMac = /Mac/i.test(navigator.platform) || /Mac OS X/i.test(ua)
	let os = 'other'

	if (isIOSDevice || isMac) {
		os = 'ios'
	} else if (/Android/i.test(ua)) {
		os = 'android'
	}

	root.dataset.deviceOs = os
})()

// =====================
// DOM Manipulation Utils
// =====================

function l_setInnerHTML(element, html) {
	element.innerHTML = html
	element.querySelectorAll('script').forEach((oldScript) => {
		const newScript = document.createElement('script')
		const attributes = [...oldScript.attributes]

		attributes.forEach((attr) => {
			newScript.setAttribute(attr.name, attr.value)
		})

		newScript.appendChild(document.createTextNode(oldScript.innerHTML))
		oldScript.parentNode.replaceChild(newScript, oldScript)
	})
}

function l_swapHtmlNode(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
	// Run pre-process callbacks on new content
	if (Array.isArray(preProcessCallbacks)) {
		preProcessCallbacks.forEach((cb) => cb?.(newContent))
	}

	// Create a new node from newContent's outerHTML (ensures scripts run)
	const wrapper = document.createElement('div')
	l_setInnerHTML(wrapper, newContent.outerHTML)
	const newNode = wrapper.firstElementChild

	// Deduplicate [id] and [form] in oldNode's subtree
	const timestampKey = Date.now()
	oldNode.querySelectorAll('[id], [form]').forEach((el) => {
		if (el.id) el.id = `${el.id}-${timestampKey}`
		if (el.form) {
			const formId = el.getAttribute('form')
			if (formId) el.setAttribute('form', `${formId}-${timestampKey}`)
		}
	})

	// Insert newNode before oldNode and hide oldNode
	oldNode.parentNode.insertBefore(newNode, oldNode)
	oldNode.style.display = 'none'

	// Run post-process callbacks
	if (Array.isArray(postProcessCallbacks)) {
		postProcessCallbacks.forEach((cb) => cb?.(newNode))
	}

	// Remove oldNode after transition
	setTimeout(() => {
		if (oldNode?.parentNode) oldNode.remove()
	}, 500)
}

function l_smoothScrollOnAnchorTags() {
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		const id = anchor.getAttribute('href').split('#')?.[1]
		if (!id) return

		anchor.addEventListener('click', (e) => {
			e.preventDefault()

			const target = document.querySelector(anchor.getAttribute('href'))
			target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
		})
	})
}

// =====================
// Focus Management & Trap Focus
// =====================

function isVisible(element) {
	if (!element) return false

	if (element.closest('[aria-hidden="true"], [hidden]')) return false

	const detailsParent = element.closest('details')
	if (detailsParent && !detailsParent.open && !detailsParent.contains(element.closest('summary'))) {
		return false
	}

	if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') {
		return false
	}

	const style = getComputedStyle(element)
	if (style.visibility === 'hidden' || style.visibility === 'collapse') return false

	if (element.getClientRects().length === 0) return false

	return true
}

function l_getFocusableElements(container) {
	const focusableSelectors = `summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe`

	return Array.from(container.querySelectorAll(focusableSelectors)).filter((el) => isVisible(el))
}

function l_disableFocusableElements(container) {
	l_getFocusableElements(container).forEach((el) => {
		el.setAttribute('tabindex', '-1')
		el.setAttribute('aria-disabled', 'true')
	})
}

function l_enableFocusableElements(container) {
	l_getFocusableElements(container).forEach((el) => {
		el.removeAttribute('tabindex')
		el.removeAttribute('aria-disabled')
	})
}

const l_trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus, enableFocusRange = true) {
	// cleanup any previous traps
	l_removeTrapFocus()

	l_trapFocusHandlers.keydown = (event) => {
		const key = event.code?.toUpperCase()
		const isTab = key === 'TAB'
		const isArrowDown = key === 'ARROWDOWN'
		const isArrowUp = key === 'ARROWUP'

		const elements = l_getFocusableElements(container)
		const [first, last] = [elements[0], elements[elements.length - 1]]
		const currentIndex = elements.indexOf(event.target)

		if (!elements.length) return

		if (isTab) {
			if ((event.target === last || last?.contains?.(event.target)) && !event.shiftKey) {
				event.preventDefault()
				first?.focus()
			} else if ((event.target === container || first?.contains?.(event.target)) && event.shiftKey) {
				event.preventDefault()
				last?.focus()
			}

			return
		}

		if (isArrowDown) {
			event.preventDefault()
			if (currentIndex === -1 || currentIndex === elements.length - 1) {
				first?.focus()
			} else {
				elements[currentIndex + 1]?.focus()
			}

			return
		}

		if (isArrowUp) {
			event.preventDefault()
			if (currentIndex === -1 || currentIndex === 0) {
				last?.focus()
			} else {
				elements[currentIndex - 1]?.focus()
			}

			return
		}
	}

	l_trapFocusHandlers.focusin = (event) => {
		if (container.contains(event.target)) {
			document.addEventListener('keydown', l_trapFocusHandlers.keydown)
		}
	}

	l_trapFocusHandlers.focusout = () => {
		document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	document.addEventListener('focusin', l_trapFocusHandlers.focusin)
	document.addEventListener('focusout', l_trapFocusHandlers.focusout)

	// Initial focus
	const elements = l_getFocusableElements(container)
	const first = elements[0]
	const elementToFocus = defaultElementToFocus || first
	if (elementToFocus) {
		elementToFocus.focus()
		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value &&
			enableFocusRange
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length)
		}
	}
}

function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', l_trapFocusHandlers.focusin)
	document.removeEventListener('focusout', l_trapFocusHandlers.focusout)
	document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	if (elementToFocus) elementToFocus.focus()
}

// =====================
// Media Controls
// =====================

function l_pauseAllMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.pauseVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
		youtubeVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
		vimeoVideo.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	container.querySelectorAll('video').forEach((video) => video.pause())
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.pause()
	})
}

function l_playMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.playVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((youtubeVideo) => {
		youtubeVideo.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((vimeoVideo) => {
		vimeoVideo.contentWindow.postMessage('{"method":"play"}', '*')
	})
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.play()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.play()
	})
}

// =====================
// Event/Utility Functions
// =====================

function l_debounce(fn, wait) {
	let t

	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function l_addEventDelegate({
	context = document.documentElement,
	event = 'click',
	selector,
	handler,
	capture = false,
}) {
	if (!context) return

	const listener = function (e) {
		// loop parent nodes from the target to the delegation node
		for (let target = e.target; target && target !== this; target = target.parentNode) {
			if (target.matches(selector)) {
				handler.call(target, e, target)
				break
			}
		}
	}

	context.addEventListener(event, listener, capture)

	return () => {
		context.removeEventListener(event, listener, capture)
	}
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

// =====================
// Money Formatting
// =====================

function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.LTATheme.money.format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', ',')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
	constructor(cartDrawer = null) {
		this.cartDrawer = cartDrawer
	}

	getBaseConfig() {
		return {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
	}

	createConfigFromForm(formEl) {
		const config = this.getBaseConfig()
		const formData = new FormData(formEl)

		config.body = formData

		return config
	}

	createConfigFromItems(items = []) {
		const config = this.getBaseConfig()
		const formData = new FormData()

		items.forEach((item, index) => {
			const prefix = `items[${index}]`

			formData.append(`${prefix}[id]`, item.id)
			formData.append(`${prefix}[quantity]`, item.quantity)

			if (item.selling_plan) {
				formData.append(`${prefix}[selling_plan]`, item.selling_plan)
			}

			// Handle properties as nested fields
			if (item.properties && typeof item.properties === 'object') {
				Object.entries(item.properties).forEach(([key, value]) => {
					if (value !== undefined && value !== null) {
						formData.append(`${prefix}[properties][${key}]`, value)
					}
				})
			}
		})

		config.body = formData
		return config
	}

	async addToCart(config, id = 'default') {
		// Note: Don't use finally / catch here

		return fetch(`${window.LTATheme.routes.cart_add_url}.js`, config)
			.then((res) => {
				return res.json()
			})
			.then((data) => {
				if (data?.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

					return data
				}

				document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}

	async changeCartLineItem(formData) {
		return fetch(`${window.LTATheme.routes.cart_change_url}.js`, { ...l_fetchConfig(), body: JSON.stringify(formData) })
			.then((res) => res.json())
			.then((data) => {
				return data
			})
	}
}

const ltaCartService = new LTACartServices()

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
		this._onResize = this._onResize.bind(this)
		this._swiperInitialized = false
	}

	connectedCallback() {
		if (window?.Swiper) {
			this.setupCarousel()
			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this._onResize)
	}

	setupCarousel() {
		const configNode = this.querySelector('[data-carousel-config]')
		if (!configNode) return

		this.carouselConfig = JSON.parse(configNode.textContent)
		if (!this.carouselConfig) return

		this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
		this._swiperInitialized = true

		// Design mode scroll
		if (window.Shopify?.designMode) {
			this.designModeScroll()
		}

		// Set up navigation top position
		const hasNavigationTarget = this.carouselConfig?.navigation && this.carouselConfig.navigation.navigationHeightTarget
		if (hasNavigationTarget) {
			this.setupNavigation()
			window.addEventListener('resize', this._onResize)
		}

		// Dispatch event
		if (this.dataset.dispatchEvent === 'true') {
			this.dispatchEvent(
				new CustomEvent('l:swiper:carousel:loaded', {
					bubbles: true,
					detail: {
						id: this.id || this.dataset.id || 'default',
					},
				})
			)
		}
	}

	_onResize() {
		// Re-setup navigation when window resizes
		if (this._swiperInitialized && this.carouselConfig?.navigation?.navigationHeightTarget) {
			this.setupNavigation()
		}
	}

	setupNavigation() {
		const navigation = this.carouselConfig?.navigation
		if (!navigation || !navigation.navigationHeightTarget) return

		const targetEl = this.querySelector(navigation.navigationHeightTarget)
		if (!targetEl) return

		const navTop = `${targetEl.offsetHeight / 2}px`

		const nextNavEls = document.querySelectorAll(navigation.nextEl)
		const prevNavEls = document.querySelectorAll(navigation.prevEl)

		nextNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
		prevNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl === swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0) {
		if (this.swiperCarousel && typeof this.swiperCarousel.slideTo === 'function') {
			this.swiperCarousel.slideTo(index)
		}
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

class LTACTAButton extends HTMLElement {
	constructor() {
		super()

		this.handleAtcBtnClick = this.handleAtcBtnClick.bind(this)
	}

	connectedCallback() {
		this.buttonEl = this.querySelector('button')
		if (this.buttonEl) {
			this.buttonEl.addEventListener('click', this.handleAtcBtnClick)
		}
	}

	disconnectedCallback() {
		if (this.buttonEl) {
			this.buttonEl.removeEventListener('click', this.handleAtcBtnClick)
		}
	}

	handleAtcBtnClick() {
		if (!this.buttonEl) return

		this.buttonEl.classList.add('is-loading', 'l-button-loading')

		const { variantId, quantity, sellingPlanId } = this.dataset
		const config = {
			id: variantId,
			quantity: parseInt(quantity),
			...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
			properties: {
				'is-package-box': 'true',
			},
		}

		const formData = ltaCartService.createConfigFromItems([config])

		ltaCartService
			.addToCart(formData, 'l-atc-button')
			.catch(console.error)
			.finally(() => {
				this.buttonEl.classList.remove('is-loading', 'l-button-loading')
			})
	}
}

customElements.define('l-atc-button', LTACTAButton)

/*****************************************
 * [UTIL]: HEIGHT ANIMATION
 ******************************************/

function l_getTransitionEndEventName() {
	let eventName
	const testElement = document.createElement('div')
	const eventNames = {
		transition: 'transitionend',
		OTransition: 'otransitionend',
		MozTransition: 'transitionend',
		WebkitTransition: 'webkitTransitionEnd',
	}

	for (eventName in eventNames) {
		if (Object.prototype.hasOwnProperty.call(eventNames, eventName) && testElement.style[eventName] !== false) {
			return eventNames[eventName]
		}
	}

	return 'transitionend'
}

function l_setElementCollapsed(element, { hidden = true } = {}) {
	if (!element) return

	element.style.height = '0px'
	element.classList.remove('is-expanded')
	element.classList.add('is-collapsed')

	if (hidden) {
		element.setAttribute('hidden', 'hidden')
		element.classList.add('l-hidden')
	}

	element.setAttribute('aria-hidden', 'true')
}

function l_setElementExpanded(element) {
	if (!element) return

	element.style.height = 'auto'
	element.style.transition = null
	element.classList.add('is-expanded')
	element.classList.remove('is-collapsed')
	element.removeAttribute('hidden')
	element.classList.remove('l-hidden')
	element.setAttribute('aria-hidden', 'false')
}

function l_animateElementExpand(element, { onComplete } = {}) {
	if (!element) return

	const transitionendEvent = l_getTransitionEndEventName()
	const wasHidden = element.hasAttribute('hidden') || element.classList.contains('l-hidden')

	const onTransitionEnd = (event) => {
		if (event.target !== element) return

		element.removeEventListener(transitionendEvent, onTransitionEnd)

		requestAnimationFrame(() => {
			element.style.transition = '0'
			element.style.height = 'auto'

			requestAnimationFrame(() => {
				element.style.height = null
				element.style.transition = null
				onComplete?.()
			})
		})
	}

	element.addEventListener(transitionendEvent, onTransitionEnd)
	element.removeAttribute('hidden')
	element.classList.remove('l-hidden')
	element.classList.add('is-expanded')
	element.classList.remove('is-collapsed')
	element.setAttribute('aria-hidden', 'false')

	if (wasHidden || element.style.height === 'auto' || !element.style.height) {
		element.style.height = '0px'
	}

	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			element.style.height = `${element.scrollHeight}px`
		})
	})
}

function l_animateElementCollapse(element, { onComplete, hideWhenDone = true } = {}) {
	if (!element) return

	const transitionendEvent = l_getTransitionEndEventName()
	const collapsedHeight = '0px'

	const onTransitionEnd = (event) => {
		if (event.target !== element) return

		element.removeEventListener(transitionendEvent, onTransitionEnd)

		if (hideWhenDone) {
			element.setAttribute('hidden', 'hidden')
			element.classList.add('l-hidden')
			element.classList.add('is-collapsed')
		}

		element.setAttribute('aria-hidden', 'true')
		onComplete?.()
	}

	element.addEventListener(transitionendEvent, onTransitionEnd)
	element.classList.remove('is-expanded')

	requestAnimationFrame(() => {
		element.style.transition = '0'
		element.style.height = `${element.scrollHeight}px`

		requestAnimationFrame(() => {
			element.style.transition = null
			element.style.height = collapsedHeight
		})
	})
}

/*****************************************
 * [COMPONENT]: COLLAPSIBLE TAB
 ******************************************/

class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerEl = this.querySelector('[data-trigger]')
		this.contentEl = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = true
		this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.contentEl.removeAttribute('hidden')
			this.contentEl.classList.remove('l-hidden')
			this.classList.add(this.expandedClass)
			this.setExpandedAria(false)
		} else {
			l_setElementCollapsed(this.contentEl, { hidden: false })
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria(null, false)
		}

		// Events
		this.triggerEl.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		const { preventDefault, onlyOpen } = this.dataset

		if (preventDefault !== 'false') {
			e.preventDefault()
		}

		const shouldOnlyOpen = onlyOpen ? onlyOpen === 'true' : true

		if (shouldOnlyOpen) {
			this.selected ? this.close() : this.open()
		} else {
			this.open()
		}

		// Ensure only one tab is open in the group if required.
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			if (tabsContainer) {
				const collapsibleTabs = tabsContainer.querySelectorAll('l-collapsible-tab')
				collapsibleTabs.forEach((tab) => {
					if (tab !== this && tab.selected && tab.close) {
						tab.close()
					}
				})
			}
		}
	}

	open() {
		this.selected = true
		this.expand()

		this.setExpandedAria()
	}

	close() {
		this.selected = false
		this.collapse()

		this.setCollapsedAria()
	}

	expand() {
		this.isOpen = true
		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)
		l_animateElementExpand(this.contentEl)
	}

	collapse() {
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		l_animateElementCollapse(this.contentEl, {
			onComplete: () => {
				if (!this.isOpen) {
					this.classList.add(this.collapsedClass)
				}
			},
		})

		if (this.isScrollIntoView) {
			l_debounce(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)()
		}
	}

	destroy() {
		this.triggerEl && this.triggerEl.removeEventListener('click', (e) => this.toggle(e))
		this.contentEl && this.contentEl.removeAttribute('aria-hidden')
		this.contentEl && (this.contentEl.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	setExpandedAria(autoFocus = true) {
		this.setAttribute('open', true)

		this.triggerEl.setAttribute('aria-expanded', 'true')
		this.contentEl.removeAttribute('aria-hidden')
		this.contentEl.removeAttribute('tabindex')

		if (autoFocus) {
			l_enableFocusableElements(this.contentEl)
			l_trapFocus(this)
		}
	}

	setCollapsedAria(elementToFocus = this.triggerEl, enableTrapFocus = true) {
		this.removeAttribute('open')

		this.triggerEl.setAttribute('aria-expanded', 'false')
		this.contentEl.setAttribute('aria-hidden', 'true')
		this.contentEl.setAttribute('tabindex', '-1')

		l_disableFocusableElements(this.contentEl)
		enableTrapFocus && l_removeTrapFocus(elementToFocus)
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/*****************************************
 * [COMPONENT]: MEDIA VIDEO
 ******************************************/

class LTAMediaVideo extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.videoContainerEl = this.querySelector('[data-video-container]') || this

		// Click
		this.addEventListener('click', (event) => {
			const hasLoaded = this.classList.contains('is-loaded')
			const isPlaying = this.classList.contains('is-playing')

			hasLoaded ? (isPlaying ? this.pauseVideo() : this.playVideo()) : this.initPlayer(event)
		})
	}

	initPlayer(event) {
		event.preventDefault()

		const templateEl = this.querySelector('template')

		if (!templateEl) return

		this.pauseOtherVideos()

		// Add template content to the element
		const templateContentEl = templateEl.content.cloneNode(true)

		this.videoContainerEl.appendChild(templateContentEl)
		this.classList.add('is-loaded', 'is-playing')
	}

	playVideo() {
		this.pauseOtherVideos()
		l_playMedia(this)

		this.classList.add('is-playing')
		this.classList.remove('is-paused')
	}

	pauseVideo() {
		l_pauseAllMedia(this)

		this.classList.remove('is-playing')
		this.classList.add('is-paused')
	}

	pauseOtherVideos() {
		l_pauseAllMedia(this.parentElement)

		this.parentElement.querySelectorAll('l-media-video').forEach((videoEl) => {
			const isLoaded = videoEl.classList.contains('is-loaded')

			if (isLoaded) {
				videoEl.classList.remove('is-playing')
				videoEl.classList.add('is-paused')
			}
		})
	}
}

customElements.define('l-media-video', LTAMediaVideo)

/*****************************************
 * [COMPONENT]: QUANTITY INPUT
 ******************************************/

if (!customElements.get('l-qty-input')) {
	class LTAQtyInput extends HTMLElement {
		constructor() {
			super()

			this.onButtonClick = this.onButtonClick.bind(this)
			this.onInputChange = this.onInputChange.bind(this)
			this.onInputBeforeChange = this.onInputBeforeChange.bind(this)
		}

		connectedCallback() {
			this.input = this.querySelector('input')
			this.buttonMinus = this.querySelector("button[name='minus']")
			this.buttonPlus = this.querySelector("button[name='plus']")

			if (!this.input) return

			this.changeEvent = new Event('change', { bubbles: true })

			this.validateQtyRules()
			this.setupEventListeners()
		}

		setupEventListeners() {
			this.input.addEventListener('input', this.onInputBeforeChange)
			this.input.addEventListener('change', this.onInputChange)
			this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick))
		}

		onInputBeforeChange() {
			const value = parseInt(this.input.value)

			if (isNaN(value)) return

			const min = parseInt(this.input.min)
			const max = parseInt(this.input.max)
			let newValue = value

			if (!isNaN(min) && value < min) {
				newValue = min
			}

			if (!isNaN(max) && value > max) {
				newValue = max
			}

			if (newValue !== value) {
				this.input.value = newValue
			}
		}

		onInputChange() {
			this.validateQtyRules()
		}

		onButtonClick(event) {
			event.preventDefault()

			const previousValue = this.input.value
			const buttonName = event.target.name

			if (buttonName === 'plus') {
				this.incrementQuantity()
			} else if (buttonName === 'minus') {
				this.decrementQuantity()
			}

			if (previousValue !== this.input.value) {
				this.input.dispatchEvent(this.changeEvent)
				this.onQuantityChanged(previousValue, parseInt(this.input.value))
			}
		}

		onQuantityChanged(oldValue, newValue) {
			// Override in child classes if needed
		}

		incrementQuantity() {
			const min = parseInt(this.input.dataset.min)
			const step = parseInt(this.input.step)
			const value = parseInt(this.input.value)

			if (min > step && value === 0) {
				this.input.value = min
			} else {
				this.input.stepUp()
			}
		}

		decrementQuantity() {
			this.input.stepDown()

			const min = this.input.dataset.min
			if (min && min === this.input.value) {
				this.input.value = parseInt(this.input.min)
			}
		}

		validateQtyRules() {
			const value = parseInt(this.input.value)

			if (this.input.min && this.buttonMinus) {
				this.buttonMinus.toggleAttribute('disabled', value <= parseInt(this.input.min))
			}

			if (this.input.max && this.buttonPlus) {
				this.buttonPlus.toggleAttribute('disabled', value >= parseInt(this.input.max))
			}
		}

		disconnectedCallback() {
			if (this.input) {
				this.input.removeEventListener('input', this.onInputBeforeChange)
				this.input.removeEventListener('change', this.onInputChange)
			}

			this.querySelectorAll('button').forEach((button) => button.removeEventListener('click', this.onButtonClick))
		}
	}

	customElements.define('l-qty-input', LTAQtyInput)
}

if (!customElements.get('l-legacy-product-quantity-input')) {
	class LTALegacyProductQuantityInput extends customElements.get('l-qty-input') {
		constructor() {
			super()
			this.onVariantChange = this.handleOnVariantChange.bind(this)
			this.onInputValueChange = this.handleInputValueChange.bind(this)
			this.onLegacyCartAddSuccess = this.handleLegacyCartAddSuccess.bind(this)
			this.onProductAdded = this.handleProductAdded.bind(this)
		}

		connectedCallback() {
			super.connectedCallback()

			if (!this.input) return

			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			this.syncQuantityToForm()
			this.input.addEventListener('change', this.onInputValueChange)
			document.addEventListener('theme:variant:change', this.onVariantChange)
			document.addEventListener('theme:cart:add:success', this.onLegacyCartAddSuccess)
			document.addEventListener('l:product:added', this.onProductAdded)
		}

		handleInputValueChange() {
			this.syncQuantityToForm()
		}

		isFormForThisQuantitySelector(form) {
			if (!form) return false

			const ourForm = this.getAddToCartForm()
			return Boolean(ourForm && form.id === ourForm.id)
		}

		handleLegacyCartAddSuccess(event) {
			if (!this.isFormForThisQuantitySelector(event.detail?.form)) return

			this.resetToMinQuantity()
		}

		handleProductAdded(event) {
			const response = event.detail?.response
			const responseProductId = response?.product_id

			if (responseProductId && String(responseProductId) !== String(this.productId)) return

			this.resetToMinQuantity()
		}

		resetToMinQuantity() {
			if (!this.input) return

			const min = parseInt(this.input.min, 10)
			const nextValue = !isNaN(min) && min > 0 ? min : 1
			const previousValue = parseInt(this.input.value, 10) || 0

			if (previousValue === nextValue) {
				this.validateQtyRules()
				this.syncQuantityToForm()
				return
			}

			this.input.value = nextValue

			if (this.changeEvent) {
				this.input.dispatchEvent(this.changeEvent)
			} else {
				this.input.dispatchEvent(new Event('change', { bubbles: true }))
			}

			this.onQuantityChanged(previousValue, nextValue)
			this.validateQtyRules()
			this.syncQuantityToForm()
		}

		getAddToCartForm() {
			const formId = this.dataset.productFormId

			if (formId) {
				return document.getElementById(formId)
			}

			return this.closest('[data-product]')?.querySelector('form[data-product-form]')
		}

		syncQuantityToForm() {
			const form = this.getAddToCartForm()
			if (!form || !this.input) return

			let syncInput = form.querySelector('[data-legacy-qty-sync]')

			if (!syncInput) {
				syncInput = document.createElement('input')
				syncInput.type = 'hidden'
				syncInput.name = 'quantity'
				syncInput.setAttribute('data-legacy-qty-sync', '')
				form.appendChild(syncInput)
			}

			syncInput.value = this.input.value
		}

		onQuantityChanged(oldValue, newValue) {
			this.syncQuantityToForm()

			this.input.dispatchEvent(
				new CustomEvent('l:quantity:changed', {
					bubbles: true,
					detail: {
						quantity: newValue,
						productId: this.productId,
						sectionId: this.sectionId,
					},
				})
			)
		}

		buildSectionRequestUrl(variantId) {
			const params = [`section_id=${this.sectionId}`]

			if (variantId) {
				params.push(`variant=${variantId}`)
			}

			return `${window.location.pathname}?${params.join('&')}`
		}

		applyQuantityRulesFromHtml(newInputEl, variant, { syncQuantityFromHtml = false, resetToMin = false } = {}) {
			if (!newInputEl || !this.input) return

			const previousVariantId = parseInt(this.dataset.selectedVariantId, 10)
			const isSameVariant = previousVariantId === variant?.id
			const previousValue = parseInt(this.input.value, 10)
			const shouldResetQuantity = Boolean(resetToMin || syncQuantityFromHtml)

			this.input.setAttribute('min', newInputEl.min)
			this.input.setAttribute('max', newInputEl.max)
			this.input.setAttribute('step', newInputEl.step)
			this.input.toggleAttribute('disabled', newInputEl.disabled)

			this.querySelectorAll('[data-qty-btn]').forEach((button, index) => {
				const newButton = newInputEl.closest('l-legacy-product-quantity-input')?.querySelectorAll('[data-qty-btn]')[
					index
				]

				if (newButton) {
					button.toggleAttribute('disabled', newButton.disabled)
				}
			})

			if (shouldResetQuantity) {
				const min = parseInt(newInputEl.min, 10) || 1
				this.input.value = min
			} else if (!isSameVariant || isNaN(previousValue)) {
				this.input.value = newInputEl.value
			} else {
				const min = parseInt(newInputEl.min, 10) || 1
				const max = parseInt(newInputEl.max, 10)
				let nextValue = Math.max(previousValue, min)

				if (!isNaN(max) && max > 0) {
					nextValue = Math.min(nextValue, max)
				}

				this.input.value = nextValue
			}

			if (variant?.id) {
				this.dataset.selectedVariantId = variant.id
			}

			const nextValue = parseInt(this.input.value, 10)

			if (shouldResetQuantity || !isSameVariant || nextValue !== previousValue) {
				this.input.dispatchEvent(this.changeEvent)

				if (nextValue !== previousValue) {
					this.onQuantityChanged(previousValue, nextValue)
				}
			}

			this.validateQtyRules()
			this.syncQuantityToForm()
		}

		async handleOnVariantChange(event) {
			const sectionEl = this.closest('.shopify-section')
			if (event.target !== sectionEl) return

			const variant = event.detail?.variant
			if (!variant) return

			const blockId = this.dataset.blockId
			if (!blockId) return

			try {
				const response = await fetch(this.buildSectionRequestUrl(variant.id))
				if (!response.ok) throw new Error(`Section fetch responded with ${response.status}`)

				const html = new DOMParser().parseFromString(await response.text(), 'text/html')
				const newInputEl = html
					.getElementById(`Product-Quantity-Selector-${blockId}`)
					?.querySelector('[data-qty-input]')

				this.applyQuantityRulesFromHtml(newInputEl, variant)
			} catch (error) {
				console.error(error)
			}
		}

		disconnectedCallback() {
			document.removeEventListener('theme:variant:change', this.onVariantChange)
			document.removeEventListener('theme:cart:add:success', this.onLegacyCartAddSuccess)
			document.removeEventListener('l:product:added', this.onProductAdded)
			this.input?.removeEventListener('change', this.onInputValueChange)
			super.disconnectedCallback()
		}
	}

	customElements.define('l-legacy-product-quantity-input', LTALegacyProductQuantityInput)
}

/*****************************************
 * [COMPONENT]: BREADCRUMB
 ******************************************/
if (!customElements.get('l-breadcrumb')) {
	class LTABreadcrumb extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			const previousUrl = localStorage.getItem('currentUrl')
			const previousLabel = localStorage.getItem('currentLabel')
			const previousTemplate = localStorage.getItem('currentTemplate')

			const currentTemplate = this.dataset.template
			const currentUrl = this.dataset.url
			const currentLabel = this.dataset.label

			if (!previousUrl || !previousLabel || !previousTemplate) return

			if (
				currentUrl !== previousUrl &&
				currentLabel !== previousLabel &&
				currentTemplate !== previousTemplate &&
				previousTemplate != 'index'
			) {
				const hiddenLinkEl = this.querySelector('[data-hidden-link]')
				const hiddenStepEl = this.querySelector('[data-hidden-step]')

				hiddenLinkEl.href = previousUrl
				hiddenLinkEl.textContent = previousLabel

				hiddenLinkEl.removeAttribute('hidden')
				hiddenStepEl.removeAttribute('hidden')

				this.querySelectorAll('.js-collection-product-el').forEach((el) => {
					el.remove()
				})
			}
		}
	}

	customElements.define('l-breadcrumb', LTABreadcrumb)
}

if (!customElements.get('l-breadcrumb-watcher')) {
	class LTABreadCrumbWatcher extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			const currentUrl = this.dataset.url
			const currentLabel = this.dataset.label
			const currentTemplate = this.dataset.template

			if (currentUrl && currentLabel && currentTemplate) {
				localStorage.setItem('currentUrl', currentUrl)
				localStorage.setItem('currentLabel', currentLabel)
				localStorage.setItem('currentTemplate', currentTemplate)
			}
		}
	}

	customElements.define('l-breadcrumb-watcher', LTABreadCrumbWatcher)
}

/************************************************************************************

* [SECTION COMPONENTS]

*************************************************************************************/
/******************************************
 * [COMPONENT]: PDP SUBSCRIPTIONS
 ******************************************/

class LTASubscriptionSellingPlans extends HTMLElement {
	constructor() {
		super()
		this._onVariantChange = this.handleOnVariantChange.bind(this)
		this._onProductQuantityChange = this.handleOnProductQuantityChange.bind(this)
		this._onChange = this.handleOnChange.bind(this)
		this._onSellingPlanSelect = this.handleSellingPlanSelect.bind(this)
		this._onMilestoneClick = this.handleMilestoneClick.bind(this)
		this._onCartUpdate = l_debounce(this.handleOnCartUpdate.bind(this), 200)
	}

	connectedCallback() {
		this.productContainer = this.getProductContainer()
		this.init()
		this.disableAppInputsIfNeeded()

		document.addEventListener('theme:variant:change', this._onVariantChange)
		document.addEventListener('theme:cart:added', this._onCartUpdate)
		document.addEventListener('theme:cart:update', this._onCartUpdate)
		document.addEventListener('rebuy:cart.change', this._onCartUpdate)
		document.addEventListener('l:product:added', this._onCartUpdate)
		this.productContainer?.addEventListener('change', this._onProductQuantityChange)
		this.addEventListener('change', this._onChange)
		this.addEventListener('click', this._onMilestoneClick)
	}

	disconnectedCallback() {
		document.removeEventListener('theme:variant:change', this._onVariantChange)
		document.removeEventListener('theme:cart:added', this._onCartUpdate)
		document.removeEventListener('theme:cart:update', this._onCartUpdate)
		document.removeEventListener('rebuy:cart.change', this._onCartUpdate)
		document.removeEventListener('l:product:added', this._onCartUpdate)
		this.productContainer?.removeEventListener('change', this._onProductQuantityChange)
		this.removeEventListener('change', this._onChange)
		this.removeEventListener('click', this._onMilestoneClick)
		this.sellingPlanSelectEl?.removeEventListener('change', this._onSellingPlanSelect)
	}

	getProductContainer() {
		return this.closest('[data-product]') || this.closest('.shopify-section')
	}

	init() {
		this.saveLabelEl = this.querySelector('[data-save-label]')
		this.variantPriceEl = this.querySelector('[data-variant-price]')
		this.sellingPriceEl = this.querySelector('[data-selling-price]')
		this.sellingComparePriceEl = this.querySelector('[data-selling-compare-price]')
		this.priceEls = this.querySelectorAll('[data-price]')
		this.sellingPlanSelectEl = this.querySelector('[data-selling-plan-select]')
		this.qtyBreaksEl = this.querySelector('[data-qty-breaks]')
		this.qtyBreaksFillEl = this.querySelector('[data-qty-breaks-fill]')
		this.qtyBreaksMessageEl = this.querySelector('[data-qty-breaks-message]')
		this.qtyBreakMilestoneEls = this.querySelectorAll('[data-qty-breaks-milestone]')
		this.subscriptionBodyEl = this.querySelector('[data-subscription-body]')
		this.maxTierPanelEl = this.querySelector('[data-max-tier-panel]')
		this.maxTierBoxEl = this.querySelector('[data-max-tier-box]')
		this.maxTierBoxContentEl = this.querySelector('[data-max-tier-box-content]')

		this.productId = this.dataset.productId
		this.sectionId = this.dataset.sectionId

		const script = this.querySelector('script[type="application/json"]')
		this.subscriptionData = {
			...(script ? JSON.parse(script.textContent) : {}),
			showQuantityBreaks: false,
			maxTierBoxAvailable: true,
		}

		this.tiers = []
		this.selectedVariantId = this.subscriptionData?.id
		this.selectedSellingPlanId = this.querySelector('[name="selling_plan"]:checked')?.value
		this.cartSubscriptionQty = parseInt(this.subscriptionData.cartSubscriptionQty, 10) || 0
		this.cartSubscriptionLineCount = parseInt(this.subscriptionData.cartSubscriptionLineCount, 10) || 0

		const inputQuantity = this.getCurrentQuantity()
		this.subscriptionData.quantity = inputQuantity

		this.hideQtyBreaks()
		this.syncSellingPlanSelect()
		this.syncPurchasePanels()
		this.initQtyBreakTiers(this.getEffectiveQuantity())
		this.updatePrices(inputQuantity)

		this.sellingPlanSelectEl?.removeEventListener('change', this._onSellingPlanSelect)
		this.sellingPlanSelectEl?.addEventListener('change', this._onSellingPlanSelect)
	}

	hideQtyBreaks() {
		this.subscriptionData.showQuantityBreaks = false
		this.qtyBreaksEl?.classList.add('l-hidden')
		this.qtyBreaksEl?.setAttribute('hidden', '')
	}

	hideMaxTierBox() {
		this.subscriptionData.maxTierBoxAvailable = false
		this.maxTierPanelEl?.classList.add('l-hidden')
		this.maxTierPanelEl?.setAttribute('hidden', '')
		this.maxTierPanelEl?.setAttribute('aria-hidden', 'true')
	}

	updateMaxTierBoxContent(savings) {
		if (!this.maxTierBoxContentEl || !this.subscriptionData.maxTierBoxMessage) return

		const resolvedSavings =
			savings ?? this.tiers[this.tiers.length - 1]?.savings ?? this.subscriptionData.maxTierDefaultSavings ?? ''

		this.maxTierBoxContentEl.innerHTML = this.formatMaxTierMessage(
			this.subscriptionData.maxTierBoxMessage,
			resolvedSavings
		)
	}

	initQtyBreakTiers(currentQuantity) {
		const isEligible = this.dataset.qtyBreaksEligible === 'true'
		const tiers = Array.isArray(this.subscriptionData.tiers) ? this.subscriptionData.tiers : []

		if (!isEligible || !tiers.length) {
			this.hideQtyBreaks()
			this.hideMaxTierBox()
			return
		}

		this.tiers = tiers
		this.subscriptionData.maxTierBoxAvailable = true
		this.qtyBreakMilestoneEls = this.querySelectorAll('[data-qty-breaks-milestone]')
		this.positionMilestones()
		this.updateMaxTierBoxContent()

		if (this.isSubscriptionSelected()) {
			this.showQtyBreaks()
		} else {
			this.hideQtyBreaks()
		}

		this.updateQtyBreaks(currentQuantity)
		this.syncPurchasePanels()
	}

	showQtyBreaks() {
		this.subscriptionData.showQuantityBreaks = true
		this.qtyBreaksEl?.classList.remove('l-hidden')
		this.qtyBreaksEl?.removeAttribute('hidden')
	}

	getCurrentQuantity() {
		const qtyInput = this.getQtyInputEl()
		return parseInt(qtyInput?.value, 10) || 1
	}

	getCartSubscriptionQty() {
		return parseInt(this.cartSubscriptionQty, 10) || 0
	}

	getEffectiveQuantity() {
		return this.getCartSubscriptionQty() + this.getCurrentQuantity()
	}

	buildProductSectionRequestUrl() {
		const params = [`section_id=${this.sectionId}`]

		if (this.selectedSellingPlanId) {
			params.push(`selling_plan=${this.selectedSellingPlanId}`)
		}

		const variantId = this.selectedVariantId || this.subscriptionData?.id
		if (variantId) {
			params.push(`variant=${variantId}`)
		}

		return `${window.location.pathname}?${params.join('&')}`
	}

	async refreshCartSubscriptionQty() {
		if (this.dataset.qtyBreaksEligible !== 'true') return

		try {
			const response = await fetch(this.buildProductSectionRequestUrl())
			if (!response.ok) throw new Error(`Section fetch responded with ${response.status}`)

			const html = new DOMParser().parseFromString(await response.text(), 'text/html')
			const script = html
				.getElementById(`Subscriptions-${this.sectionId}`)
				?.querySelector('script[type="application/json"]')

			if (!script) return

			const data = JSON.parse(script.textContent)
			this.cartSubscriptionQty = parseInt(data.cartSubscriptionQty, 10) || 0
			this.cartSubscriptionLineCount = parseInt(data.cartSubscriptionLineCount, 10) || 0
			this.subscriptionData = {
				...this.subscriptionData,
				cartSubscriptionQty: this.cartSubscriptionQty,
				cartSubscriptionLineCount: this.cartSubscriptionLineCount,
			}

			if (this.subscriptionData.showQuantityBreaks) {
				this.updateQtyBreaks(this.getEffectiveQuantity())
			}

			this.updatePrices(this.getCurrentQuantity())
		} catch (error) {
			console.error(error)
		}
	}

	handleOnCartUpdate() {
		this.refreshCartSubscriptionQty()
	}

	getQtyInputEl() {
		const legacyInput = this.getProductContainer()?.querySelector(
			`l-legacy-product-quantity-input[data-section-id="${this.sectionId}"] [data-qty-input]`
		)

		return legacyInput || this.getProductContainer()?.querySelector('[name="quantity"]')
	}

	setQuantity(quantity) {
		const qtyInput = this.getQtyInputEl()
		if (!qtyInput) return

		const previousValue = qtyInput.value
		const nextValue = parseInt(quantity, 10) || 1
		qtyInput.value = nextValue
		qtyInput.dispatchEvent(new Event('change', { bubbles: true }))

		const qtyComponent = qtyInput.closest('l-legacy-product-quantity-input, l-qty-input')
		if (typeof qtyComponent?.onQuantityChanged === 'function') {
			qtyComponent.onQuantityChanged(previousValue, nextValue)
		}
	}

	getUnlockedTier(quantity) {
		return [...this.tiers].reverse().find((tier) => quantity >= tier.quantity) || null
	}

	getNextTier(quantity) {
		return this.tiers.find((tier) => quantity < tier.quantity) || null
	}

	getMilestonePosition(index) {
		const count = this.tiers.length
		const startPercent = 30
		const spanPercent = 70

		if (count <= 1) return startPercent

		return startPercent + (index / (count - 1)) * spanPercent
	}

	positionMilestones() {
		this.qtyBreakMilestoneEls.forEach((milestoneEl, index) => {
			milestoneEl.style.left = `${this.getMilestonePosition(index)}%`
		})
	}

	getTierFillPercent(quantity) {
		if (!this.tiers.length) return 0
		if (quantity <= 0) return 0

		const maxTier = this.tiers[this.tiers.length - 1]
		if (quantity >= maxTier.quantity) return 100

		const firstTier = this.tiers[0]
		if (quantity < firstTier.quantity) {
			const firstPos = this.getMilestonePosition(0)
			return (quantity / firstTier.quantity) * firstPos
		}

		for (let index = 1; index < this.tiers.length; index++) {
			const tier = this.tiers[index]
			const prevTier = this.tiers[index - 1]

			if (quantity < tier.quantity) {
				const prevPos = this.getMilestonePosition(index - 1)
				const currPos = this.getMilestonePosition(index)
				const segmentProgress = (quantity - prevTier.quantity) / (tier.quantity - prevTier.quantity)

				return prevPos + segmentProgress * (currPos - prevPos)
			}
		}

		return 100
	}

	formatUnlockMessage(template, count, savings) {
		if (!template) return ''

		return template
			.replace(/\[count\]/g, `<strong>${count}</strong>`)
			.replace(/\[savings\]/g, `<strong>${savings}%</strong>`)
	}

	formatMaxTierMessage(template, savings) {
		if (!template) return ''

		return template.replace(/\[savings\]/g, String(savings))
	}

	isSubscriptionSelected() {
		const checkedOption = this.querySelector('[data-purchase-option][name="selling_plan"]:checked')
		return Boolean(checkedOption?.value)
	}

	setPanelExpanded(element, expanded) {
		if (!element) return

		if (expanded) {
			l_setElementExpanded(element)
		} else {
			l_setElementCollapsed(element)
		}
	}

	animatePanel(element, expanded) {
		if (!element) return

		const isExpanded =
			element.classList.contains('is-expanded') &&
			!element.hasAttribute('hidden') &&
			!element.classList.contains('l-hidden')

		if (expanded && isExpanded) return
		if (!expanded && element.hasAttribute('hidden')) return

		if (expanded) {
			l_animateElementExpand(element)
		} else {
			l_animateElementCollapse(element)
		}
	}

	syncPurchasePanels({ animate = false } = {}) {
		const isSubscription = this.isSubscriptionSelected()
		const shouldShowMaxTier =
			!isSubscription &&
			Boolean(this.subscriptionData.maxTierBoxMessage) &&
			this.subscriptionData.maxTierBoxAvailable !== false

		if (shouldShowMaxTier) {
			this.updateMaxTierBoxContent()
		}

		if (animate) {
			this.animatePanel(this.subscriptionBodyEl, isSubscription)
			this.animatePanel(this.maxTierPanelEl, shouldShowMaxTier)
			return
		}

		this.setPanelExpanded(this.subscriptionBodyEl, isSubscription)
		this.setPanelExpanded(this.maxTierPanelEl, shouldShowMaxTier)
	}

	updateQtyBreaks(quantity, { animatePanels = false } = {}) {
		if (animatePanels) {
			this.syncPurchasePanels({ animate: true })
		}

		if (!this.subscriptionData.showQuantityBreaks || !this.qtyBreaksEl) return

		const fillPercent = this.getTierFillPercent(quantity)
		const unlockedTier = this.getUnlockedTier(quantity)
		const nextTier = this.getNextTier(quantity)
		const maxTier = this.tiers[this.tiers.length - 1]
		const isMaxTierUnlocked = maxTier && quantity >= maxTier.quantity

		if (this.qtyBreaksFillEl) {
			this.qtyBreaksFillEl.style.width = `${fillPercent}%`
		}

		this.qtyBreakMilestoneEls.forEach((milestoneEl) => {
			const tierQty = parseInt(milestoneEl.dataset.quantity, 10)
			milestoneEl.classList.toggle('is-unlocked', quantity >= tierQty)
		})

		if (!this.qtyBreaksMessageEl) return

		if (isMaxTierUnlocked && unlockedTier) {
			this.qtyBreaksMessageEl.classList.add('is-max-tier')
			this.qtyBreaksMessageEl.innerHTML = this.formatMaxTierMessage(
				this.subscriptionData.maxTierMessage,
				unlockedTier.savings
			)
			return
		}

		this.qtyBreaksMessageEl.classList.remove('is-max-tier')

		if (nextTier) {
			// Show the next threshold qty (3 / 6 / 9), not units remaining
			this.qtyBreaksMessageEl.innerHTML = this.formatUnlockMessage(
				this.subscriptionData.unlockMessage,
				nextTier.quantity,
				nextTier.savings
			)
			return
		}

		this.qtyBreaksMessageEl.innerHTML = ''
	}

	updatePrices(quantity) {
		const { variantPrice, sellingPlanPrice, sellingPlanCompareAtPrice, priceAdjustmentType, saveLabel } =
			this.subscriptionData

		const qty = parseInt(quantity, 10) || 1
		const baseVariantPrice = parseInt(variantPrice, 10) || 0
		const baseSellingPlanPrice = parseInt(sellingPlanPrice, 10) || 0
		const baseSellingPlanCompareAtPrice = parseInt(sellingPlanCompareAtPrice, 10) || 0

		// Qty-break tier is based on cart subscription qty + PDP qty (e.g. 3 + 3 = 6)
		const unlockedTier = this.tiers?.length ? this.getUnlockedTier(this.getEffectiveQuantity()) : null
		const savings = parseFloat(unlockedTier?.savings) || 0
		const discountedSellingPlanPrice =
			savings > 0 ? Math.round((baseSellingPlanPrice * (100 - savings)) / 100) : baseSellingPlanPrice
		const compareAtUnit = Math.max(baseSellingPlanCompareAtPrice, baseSellingPlanPrice)
		const showCompareAt = compareAtUnit > discountedSellingPlanPrice

		if (this.variantPriceEl) this.variantPriceEl.innerHTML = l_formatMoney(baseVariantPrice * qty)
		if (this.sellingPriceEl) this.sellingPriceEl.innerHTML = l_formatMoney(discountedSellingPlanPrice * qty)
		if (this.sellingComparePriceEl) {
			this.sellingComparePriceEl.innerHTML = showCompareAt ? l_formatMoney(compareAtUnit * qty) : ''
		}

		if (priceAdjustmentType == 'price' && this.saveLabelEl) {
			const reducedPrice = l_formatMoney((compareAtUnit - discountedSellingPlanPrice) * qty)
			this.saveLabelEl.innerHTML = saveLabel.replace('[save_amount]', reducedPrice)
		}
	}

	handleOnProductQuantityChange(event) {
		const target = event.target
		const isLegacyQtyInput = target?.matches('[data-qty-input]')
		const isNamedQuantity = target?.getAttribute('name') === 'quantity'

		if (!isLegacyQtyInput && !isNamedQuantity) return
		if (!this.getProductContainer()?.contains(target)) return

		const inputQuantity = parseInt(target.value, 10) || 1
		this.subscriptionData = {
			...this.subscriptionData,
			quantity: inputQuantity,
		}

		this.updatePrices(inputQuantity)

		if (this.subscriptionData.showQuantityBreaks) {
			this.updateQtyBreaks(this.getEffectiveQuantity())
		}
	}

	async handleOnVariantChange(event) {
		const sectionEl = this.closest('.shopify-section')
		if (event.target !== sectionEl) return

		const variant = event.detail?.variant
		this.classList.toggle('l-hidden', !variant)
		if (!variant) return

		this.selectedVariantId = variant.id
		const preservedSellingPlanId = this.selectedSellingPlanId

		try {
			const response = await fetch(this.buildProductSectionRequestUrl())
			if (!response.ok) throw new Error(`Section fetch responded with ${response.status}`)

			const html = new DOMParser().parseFromString(await response.text(), 'text/html')
			const newSubscriptionsEl = html.getElementById(`Subscriptions-${this.sectionId}`)

			if (newSubscriptionsEl) {
				this.innerHTML = newSubscriptionsEl.innerHTML
				this.init()
				this.restorePurchaseOption(preservedSellingPlanId)
			} else {
				this.classList.add('l-hidden')
			}
		} catch (error) {
			console.error(error)
		}
	}

	restorePurchaseOption(sellingPlanId) {
		const selector = sellingPlanId
			? `[name="selling_plan"][value="${CSS.escape(String(sellingPlanId))}"]`
			: '[name="selling_plan"][value=""]'
		const radio = this.querySelector(selector)

		if (radio && !radio.checked) {
			radio.checked = true
			this.selectedSellingPlanId = sellingPlanId || null
			this.syncSellingPlanSelect()
		}

		this.updateQtyBreaks(this.getEffectiveQuantity(), { animatePanels: true })
	}

	syncSellingPlanSelect() {
		if (!this.sellingPlanSelectEl) return

		const checkedRadio = this.querySelector('[data-purchase-option][name="selling_plan"]:checked:not([value=""])')
		if (checkedRadio?.value) {
			this.sellingPlanSelectEl.value = checkedRadio.value
		}
	}

	handleSellingPlanSelect(event) {
		const sellingPlanId = event.target.value
		const matchingRadio = this.querySelector(
			`[data-purchase-option][name="selling_plan"][value="${CSS.escape(sellingPlanId)}"]`
		)

		if (matchingRadio) {
			matchingRadio.checked = true
			matchingRadio.dispatchEvent(new Event('change', { bubbles: true }))
		}
	}

	handleMilestoneClick(event) {
		const milestoneEl = event.target.closest('[data-qty-breaks-milestone]')
		if (!milestoneEl || !this.contains(milestoneEl)) return

		const tierQuantity = parseInt(milestoneEl.dataset.quantity, 10)
		if (!tierQuantity) return

		const inputQuantity = Math.max(1, tierQuantity - this.getCartSubscriptionQty())
		this.setQuantity(inputQuantity)
	}

	handleOnChange(event) {
		const target = event.target
		if (target?.getAttribute('name') !== 'selling_plan') return

		this.selectedSellingPlanId = target.value
		this.syncSellingPlanSelect()
		this.dispatchSellingPlanIdChanged()

		if (!this.isSubscriptionSelected()) {
			this.hideQtyBreaks()
		} else if (this.dataset.qtyBreaksEligible === 'true' && !this.subscriptionData.showQuantityBreaks) {
			this.initQtyBreakTiers(this.getEffectiveQuantity())
		}

		this.syncPurchasePanels({ animate: true })

		if (this.subscriptionData.showQuantityBreaks) {
			this.updateQtyBreaks(this.getEffectiveQuantity())
		}
	}

	dispatchSellingPlanIdChanged() {
		this.dispatchEvent(
			new CustomEvent('l:sellingPlan:changed', {
				bubbles: true,
				detail: {
					sellingPlanId: this.selectedSellingPlanId,
					productId: this.productId,
					sectionId: this.sectionId,
					selectedVariantId: this.selectedVariantId,
					target: this,
				},
			})
		)
	}

	disableAppInputsIfNeeded() {
		document.addEventListener('sealsubs:subscription_widget_created', function () {
			const productEl = document.getElementById('product')
			if (!productEl) return
			const sealEls = productEl.querySelectorAll('.sealsubs-target-element')
			sealEls.forEach((sealEl) => {
				sealEl.classList.add('l-hidden')
				const sellingPlanInputs = sealEl.querySelectorAll('[name="selling_plan"]')
				sellingPlanInputs.forEach((input) => {
					input.setAttribute('disabled', 'disabled')
				})
			})
		})
	}
}

customElements.define('l-subscriptions-selling-plans', LTASubscriptionSellingPlans)

/*****************************************
 * [SECTION]: BUILD A BOX SUBSCRIPTIONS
 ******************************************/

class LTABuildABoxSubscriptions extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.selectedOptionsEl = this.querySelector('[data-selected-options]')
		this.quantityBadgesEl = this.querySelector('[data-quantity-badges]')
		this.sellingPlansEl = this.querySelector('[data-selling-plans]')

		this.tagsEl = this.querySelector('[data-tags]')
		this.productGridEl = this.querySelector('[data-products]')
		this.paginationEl = this.querySelector('[data-pagination]')

		this.atcEl = this.querySelector('[data-atc]')
		this.atcTotalEl = this.atcEl.querySelector('[data-atc-total]')
		this.atcButtonEl = this.atcEl.querySelector('[data-atc-button]')

		// Data
		this.currentSetting = null
		this.selectedProducts = new Map()

		this.fetchBundleData()
	}

	fetchBundleData() {
		const fetchURL = `https://www.skinnymixes.com/tools/bundle-subscriptions/api/bundle/${this.dataset.bundleId}`

		fetch(fetchURL)
			.then((res) => res.json())
			.then((data) => {
				this.init(data)
			})
			.catch((err) => {
				console.error(err)
			})
			.finally(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'start' })
			})
	}

	init(data) {
		this.classList.remove('is-loading')

		// Data
		this._collectionData(data)

		// Render UI
		this._setPaginationData()
		this._togglePagination()
		this._renderProducts()
		this._renderQuantityBadges()
		this._renderSelectionOptions()
		this._setMaxValueForQtyInputs()
		this._renderTags()

		// Reset
		this._resetData()

		// Events
		this.quantityBadgesEl.addEventListener('change', this.onQuantityChanged.bind(this))
		this.productGridEl.addEventListener('change', this.onProductGridChange.bind(this))
		this.paginationEl.querySelector('button').addEventListener('click', this.onLoadMore.bind(this))

		l_addEventDelegate({
			contentEl: this.selectedOptionsEl,
			selector: '[data-selected-product]',
			event: 'click',
			handler: this.onRemoveSelectedProduct.bind(this),
		})

		l_addEventDelegate({
			contentEl: this.tagsEl,
			selector: '[data-tag]',
			event: 'click',
			handler: this.onFilterProductsByTag.bind(this),
		})

		this.atcButtonEl.addEventListener('click', this.onAddToCart.bind(this))
	}

	_collectionData(data) {
		// Bundle Data
		this.bundleData = data
		this.allProductsData = []
		this.currentSetting = this.bundleData.bundle.Settings.reduce((a, b) => (+a.Size < +b.Size ? a : b))

		this.bundleData.productsInSections.forEach((productsInSection) => {
			// Collection
			productsInSection.productsInCollections.forEach((productInCollection) => {
				this.allProductsData.push(...productInCollection)
			})

			// Individual products
			// Update when needed
		})

		this.currentProductsData = this.allProductsData
	}

	/**********************************
	 * [MODULES]: HEADER
	 *********************************/
	onQuantityChanged() {
		const selectedInputEl = this.quantityBadgesEl.querySelector('input:checked')
		this.currentSetting = this.bundleData.bundle.Settings.find((setting) => setting.Size == selectedInputEl.value)

		this.selectedProducts.clear()
		this._setMaxValueForQtyInputs()
		this._renderSelectionOptions()
		this._calculateBundlePrice()
	}

	onRemoveSelectedProduct(event) {
		const productRemoveEl = event.target.closest('[data-selected-product]')
		if (!productRemoveEl) return

		const productId = productRemoveEl.dataset.selectedProduct
		const product = this.selectedProducts.get(productId)

		if (!product) return

		// Update selectedProducts map
		if (product.qty > 1) {
			this.selectedProducts.set(productId, {
				...product,
				qty: product.qty - 1,
			})
		} else {
			this.selectedProducts.delete(productId)
		}

		// Update quantity input related to the removed product
		const inputEl = this.productGridEl.querySelector(`l-qty-input input[data-product-id="${productId}"]`)
		if (inputEl) {
			const currentValue = parseInt(inputEl.value, 10) || 0
			const newValue = Math.max(0, currentValue - 1)
			inputEl.value = newValue
			inputEl.dispatchEvent(new Event('change', { bubbles: true }))
		}
	}

	_renderQuantityBadges() {
		this.quantityBadgesEl.querySelectorAll('label').forEach((badgeEl) => {
			const labelEl = badgeEl.querySelector('[data-label]')
			const savingsEl = badgeEl.querySelector('[data-savings]')
			const inputEl = badgeEl.querySelector('input')

			const matchSetting = this.bundleData.bundle.Settings.find((setting) => setting.Label == labelEl.textContent)

			if (matchSetting) {
				const { DynamicDiscountType, DynamicDiscountAmount } = matchSetting
				const originalText = savingsEl.textContent
				const saveText =
					DynamicDiscountType == 'percentage'
						? `SAVE ${matchSetting.DynamicDiscountAmount}%`
						: `${l_formatMoney(DynamicDiscountAmount * 100)}`

				savingsEl.textContent = originalText != '' ? originalText.replace('[save]', saveText) : saveText

				savingsEl.classList.remove('is-loading')
				inputEl.value = matchSetting.Size
			}
		})
	}

	_setMaxValueForQtyInputs() {
		const maxSize = Number(this.currentSetting.Size) || 0

		// Calculate the total number of selected items
		const selectedQty = Array.from(this.selectedProducts.values()).reduce((sum, prod) => sum + (prod.qty || 0), 0)

		const availableQty = maxSize - selectedQty

		this.querySelectorAll('l-qty-input input').forEach((inputEl) => {
			const currentValue = Number(inputEl.value) || 0
			const originalMax = Number(inputEl.dataset.max) || 0
			const productId = inputEl.dataset.productId

			// Determine the new max for this input
			let newMax

			// When no available quantity left, only allow reducing (not increasing)
			if (availableQty === 0) {
				newMax = currentValue
			} else {
				const allowedMax = availableQty + currentValue
				newMax = originalMax > 0 ? Math.min(originalMax, allowedMax) : allowedMax
			}

			inputEl.max = newMax

			// If product is not selected, ensure the input value is 0
			if (!this.selectedProducts.has(productId)) {
				inputEl.value = 0
			} else {
				inputEl.value = this.selectedProducts.get(productId).qty
				inputEl.closest('[data-product-card]').classList.add('is-selected')
			}

			// Run quantity validation if method exists
			if (inputEl.parentElement && typeof inputEl.parentElement.validateQtyRules === 'function') {
				inputEl.parentElement.validateQtyRules()
			}
		})
	}

	_renderSelectionOptions() {
		let selectedOptionsHTML = ''
		let renderedQty = 0

		const selectedQtyArray = [...this.selectedProducts.values()]
		const maxSize = parseInt(this.currentSetting.Size)

		this.selectedOptionsEl.dataset.maxSize = maxSize

		for (let index = 0; index < maxSize; index++) {
			const matchProduct = selectedQtyArray[index]
			const maxQty = matchProduct?.qty || 1

			for (let _index = 0; _index < maxQty; _index++) {
				renderedQty++
				if (renderedQty > maxSize) break

				selectedOptionsHTML += `
        <div ${matchProduct && `data-selected-product="${matchProduct.id}"`} class="l-sBABox__selectedOptions-item l-column l-relative ${matchProduct ? '' : 'is-placeholder'}">
          <div class="l-sBABox__selectedOptions-media l-media-ratio">${matchProduct?.mediaHTML || ''}</div>
          <span class="l-sBABox__selectedOptions-name l-block l-text l-block-spaces">${matchProduct?.nameHTML || ''}</span>
          ${matchProduct ? `<button class="l-sBABox__selectedOptions-close l-button-reset l-sq l-cursor-pointer l-text l-link-tap l-inline-flex-center l-absolute-position l-zindex-2">X</button>` : ''}
        </div>`
			}
		}

		this.selectedOptionsEl.innerHTML = selectedOptionsHTML
	}

	_resetSelectedProducts() {
		this.selectedProducts.clear()
		this._renderSelectionOptions()
		this._calculateBundlePrice()
	}

	_resetData() {
		this.quantityBadgesEl.querySelector('input').checked = true
		this.sellingPlansEl.querySelector('input').checked = true
		this._resetSelectedProducts()
	}

	/**********************************
	 * [MODULES]: TAGS
	 *********************************/

	onFilterProductsByTag(event) {
		const tagEl = event.target.closest('[data-tag]')
		if (!tagEl) return

		// Update active class on tags
		this.tagsEl.querySelectorAll('[data-tag]').forEach((_tagEl) => {
			_tagEl.classList.toggle('is-active', _tagEl === tagEl)
		})

		const selectedTag = tagEl.dataset.filterValue
		this.currentProductsData = selectedTag
			? this.allProductsData.filter((product) => product.productData.tags.some((tag) => tag.includes(selectedTag)))
			: this.allProductsData

		this._renderProducts()
		this._setPaginationData()
		this._togglePagination()
		this._resetSelectedProducts()
	}

	_renderTags() {
		let renderedTagsHTML = ''
		const buildABoxFilterGroups = this.bundleData.bundle.BuildABoxFilterGroups

		buildABoxFilterGroups.forEach((buildABoxFilterGroup) => {
			buildABoxFilterGroup.Filters.forEach(({ FilterType, FilterValue, Name }) => {
				renderedTagsHTML += `
          <button data-tag data-filter-value="${FilterValue}" data-filter-type="${FilterType}" class="l-sBABox__tags-button l-button-reset l-padding l-text l-link-tap l-padding l-relative swiper-slide">
          ${Name}
          </button>
        `
			})
		})

		this.tagsEl.innerHTML = `
      <l-swiper-carousel class="l-sBABox__tags-swiper l-block swiper">
				<div class="swiper-wrapper">
          <button data-tag class="l-sBABox__tags-button l-button-reset l-padding l-text l-link-tap l-padding l-relative swiper-slide is-active">
            ALL FLAVORS
          </button>
          ${renderedTagsHTML}
				</div>

				<script type="application/json" data-carousel-config>
					{
						"slidesPerView": "auto",
						"spaceBetween": 12,
            "centeredSlides": true,
				    "centerInsufficientSlides": true,
				    "centeredSlidesBounds": true
					}
				</script>
			</l-swiper-carousel>
    `
	}

	/**********************************
	 * [MODULES]: PRODUCT CARDS
	 *********************************/
	onProductGridChange(event) {
		const inputEl = event.target
		const productCardEl = inputEl.closest('[data-product-card]')

		const productQty = Number(inputEl.value) || 0
		const productId = productCardEl.dataset.productId

		const maxSize = Number(this.currentSetting.Size) || 0
		const isValidProduct = productQty > 0 && productQty <= maxSize

		if (isValidProduct) {
			const mediaHTML = productCardEl.querySelector('[data-media]')
			const nameHTML = productCardEl.querySelector('[data-name]')
			const matchProductData = this.currentProductsData.find((product) => `${product.id}` == productId)

			this.selectedProducts.set(productId, {
				id: productId,
				mediaHTML: mediaHTML ? mediaHTML.innerHTML : '',
				nameHTML: nameHTML ? nameHTML.textContent : '',
				qty: productQty,
				// Add to cart data
				product: matchProductData,
			})
		} else {
			this.selectedProducts.delete(productId)
		}

		this._setMaxValueForQtyInputs()
		this._renderSelectionOptions()
		this._calculateBundlePrice()
	}

	_renderProducts() {
		this.productGridEl.classList.add('is-loading')

		const products = this.currentProductsData.slice(
			0,
			this.paginationData.currentPage * this.paginationData.itemsPerPage
		)

		const productCardsHTML = products.map((product) => this._renderProductCard(product)).join('')
		this.productGridEl.innerHTML = productCardsHTML

		l_debounce(() => {
			this.productGridEl.classList.remove('is-loading')
		}, 1000)()
	}

	_renderProductCard({ productData }) {
		const defaultVariant = productData.variants[0]
		const firstPreviewImage = productData.media[0].preview_image

		return `
      <div
        class="l-sBABoxCard l-column l-flex l-flex-col" data-product-card
        data-product-id="${productData.id}"
      >
        <div class="l-sBABoxCard__media l-relative l-overflow-hidden l-block-spaces">
          <picture class="l-sBABoxCard__mainMedia l-flex l-media-ratio l-text-none l-overflow-hidden" data-media>
            ${
							firstPreviewImage &&
							`
              <img
                src="${firstPreviewImage.src}&amp;width=2048"
                alt="${productData.title}"
                srcset="
                  ${firstPreviewImage.src}&amp;width=320   320w,
                  ${firstPreviewImage.src}&amp;width=480   480w,
                  ${firstPreviewImage.src}&amp;width=640   640w,
                  ${firstPreviewImage.src}&amp;width=750   750w,
                  ${firstPreviewImage.src}&amp;width=900   900w,
                "
                width="${firstPreviewImage.width}"
                height="${firstPreviewImage.height}"
                sizes="(max-width: 575px) calc(min(100vw, 1440px) / 50%_2),(max-width: 1023px) calc(min(100vw, 1440px) / 50%_2),(min-width: 1024px) calc(min(100vw, 1440px) / 25%_4)"
                fetchpriority="auto"
                loading="eager"
                class="l-sq-full"
              />
              `
						}
          </picture>

          <a
            href="${window.LTATheme.routes.root}products/${productData.handle}"
            aria-label="${productData.title}"
            class="l-sBABoxCard__media-link l-link-overlay l-zindex-3"
          ></a>
        </div>

        <div class="l-sBABoxCard__content l-w-full l-mx-auto l-flex-1 l-flex l-flex-col">
          <div class="l-sBABoxCard__name l-text l-heading-font l-text-center l-block-spaces" data-name>${productData.title}</div>
          <div class="l-sBABoxCard__price l-text l-text-center l-block-spaces">${l_formatMoney(defaultVariant.price)}</div>
          <l-qty-input class="l-sBABoxCard__qty l-flex-center">
            <button
              class="l-sBABoxCard__qty-btn is-decrease l-text l-button-reset l-sq l-inline-flex-center"
              name="minus"
              type="button"
              data-qty-btn
              ${defaultVariant.available == false && 'disabled'}
            >
              -
            </button>

            <input
              id="Quantity-${defaultVariant.id}"
              data-quantity-input
              class="l-sBABoxCard__qty-input l-input l-text-center l-sq l-text"
              type="number"
              value="0"
              step="${defaultVariant.quantity_rule.increment || 1}"
              min="0"
              data-product-id="${productData.id}"
              ${defaultVariant.quantity_rule.max ? `data-max="${defaultVariant.quantity_rule.max}" max="${defaultVariant.quantity_rule.max}"` : ''}
              ${defaultVariant.available == false ? 'disabled' : ''}
            >

            <button
              class="l-sBABoxCard__qty-btn is-increase l-text l-button-reset l-sq l-inline-flex-center"
              name="plus"
              type="button"
              data-qty-btn
              ${defaultVariant.available == false && 'disabled'}
            >
              +
            </button>
          </l-qty-input>
        </div>
      </div>
    `
	}

	/**********************************
	 * [MODULES]: PAGINATION
	 *********************************/
	onLoadMore() {
		this.paginationData.currentPage++

		this._renderProducts()
		this._togglePagination()
		this._setMaxValueForQtyInputs()
	}

	_setPaginationData() {
		this.paginationData = {
			currentPage: 1,
			totalPages: Math.ceil(this.currentProductsData.length / 16),
			itemsPerPage: 16,
		}
	}

	_togglePagination() {
		this.paginationEl.classList.toggle('l-hidden', this.paginationData.currentPage >= this.paginationData.totalPages)
	}

	/**********************************
	 * [MODULES]: ADD TO CART
	 *********************************/
	_calculateBundlePrice() {
		const maxSize = Number(this.currentSetting.Size) || 0

		const selectedProducts = [...this.selectedProducts.values()]
		const totalSelectedProductQty = selectedProducts.reduce((acc, product) => acc + product.qty, 0)

		const totalPrice = selectedProducts.reduce(
			(acc, product) => acc + product.product.productData.variants[0].price * product.qty,
			0
		)

		this.atcTotalEl.textContent = l_formatMoney(totalPrice)

		if (totalSelectedProductQty < maxSize) {
			this.atcButtonEl.setAttribute('disabled', '')
		} else {
			this.atcButtonEl.removeAttribute('disabled')
		}
	}

	onAddToCart() {
		// Handle UI
		this.atcButtonEl.classList.add('is-loading')

		// Handle Data
		const selectedProducts = [...this.selectedProducts.values()]
		const selectedSellingPlanId = this.sellingPlansEl.querySelector('input:checked').value
		const bundleID = `${Math.random().toString(36).slice(2, 18)}`
		const addToCartLineItems = selectedProducts.map((product) => ({
			id: product.product.productData.variants[0].id,
			quantity: product.qty,
			properties: {
				_bundleUid: bundleID,
				_bundleHandle: this.bundleData.bundle.Handle,
				_bundleType: 'dynamicBaB',
				_bundleProductId: this.bundleData.bundle.ProductId,
				_bundleName: this.bundleData.bundle.Name,
				_variantId: this.currentSetting.VariantId,
				_bundleLabel: this.currentSetting.Label,
				_bundleBoxSize: this.currentSetting.Size,
			},
			selling_plan: selectedSellingPlanId,
		}))

		const formData = ltaCartService.createConfigFromItems(addToCartLineItems)
		let hasError = false

		// Add To Cart
		ltaCartService.addToCart(formData).catch((err) => {
			console.error(err.message)

			hasError = true
		})

		// Update Discounts
		fetch(`${window.LTATheme.routes.cart_update_url}`, {
			...l_fetchConfig(),
			body: JSON.stringify({ discount: this.currentSetting.DynamicDiscountCode }),
		})
			.then((response) => response.json())
			.catch((error) => {
				hasError = true
			})

		// Redirect to checkout
		l_debounce(() => {
			this.atcButtonEl.classList.remove('is-loading')

			window.location.href = `${window.LTATheme.routes.root}checkout`
		}, 2000)()
	}
}

customElements.define('l-build-a-box-subscriptions', LTABuildABoxSubscriptions)

/************************************************************************************

* [GLOBAL FUNCTIONALITIES]

*************************************************************************************/

function l_openRebuyCartDrawer() {
	const cartToggleEls = document.querySelectorAll('.js-header-cart-toggle')

	cartToggleEls.forEach((cartToggleEl) => {
		cartToggleEl.addEventListener('click', (event) => {
			event.preventDefault()

			if (cartToggleEl.hasAttribute('data-redirect-to-cart-page')) {
				window.location.href = window.LTATheme.routes.cart_url
				return
			}

			window.Rebuy?.SmartCart?.show?.()
		})
	})
}

function l_initializeGlobalEventListeners() {
	// Initialize on DOM ready
	window.addEventListener('DOMContentLoaded', () => {
		l_smoothScrollOnAnchorTags()
		l_openRebuyCartDrawer()
	})
}

l_initializeGlobalEventListeners()

